import clsx from "clsx";
import { X } from "lucide-react";
import { observer } from "mobx-react-lite";
import React, { useEffect, useMemo, useRef, useState } from "react";

import { foldText } from "@/utils/text";

import { Badge } from "../base/badge";
import { BaseInput } from "./base-input";
import { ComboboxOption } from "./combobox";
import styles from "./combobox.module.css";
import { FieldProps, useModelBinding } from "./utils";

const EMPTY_ARRAY: never[] = [];

interface Props<M extends object, V> extends FieldProps<M, V[]> {
  options?: ComboboxOption<V>[];
  placeholder?: string;
}

export const ComboboxMultiple = observer(function ComboboxMultiple<
  M extends object,
  V,
>({ options = [], placeholder, ...restProps }: Props<M, V>) {
  const [filterText, setFilterText] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const blurTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return () => {
      if (blurTimerRef.current !== null) clearTimeout(blurTimerRef.current);
    };
  }, []);

  const { value: bindingValue, onChange } = useModelBinding(restProps);
  const value: V[] = bindingValue ?? EMPTY_ARRAY;

  const filteredOptions = useMemo(() => {
    if (!filterText) return options;
    const query = foldText(filterText);
    return options.filter((opt) => foldText(opt.label).includes(query));
  }, [options, filterText]);

  const selectedOptions = useMemo(() => {
    return value
      .map((v) => options.find((o) => o.value === v))
      .filter((v) => !!v);
  }, [value, options]);

  const handleSelect = (optionValue: V) => {
    onChange(
      value?.includes(optionValue)
        ? value.filter((v) => v !== optionValue)
        : [...(value || []), optionValue],
    );
    setFilterText("");
  };

  const handleRemove = (optionValue: V) => {
    onChange(value.filter((v) => v !== optionValue));
  };

  const handleClear = () => {
    onChange([]);
  };

  return (
    <div style={{ position: "relative" }}>
      <div className={clsx(styles.control, styles.controlMulti)}>
        <div className="flex flex-wrap grow">
          {selectedOptions.map((option) => (
            <Badge
              key={String(option.value)}
              variant="secondary"
              className="m-[1px] gap-1 pr-1"
            >
              {option.label}
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  handleRemove(option.value);
                }}
                className="hover:bg-muted-foreground/20 rounded-full p-0.5"
              >
                <X className="size-3" />
              </button>
            </Badge>
          ))}
          <BaseInput
            className={styles.input}
            type="text"
            value={filterText}
            onChange={(e) => setFilterText(e.target.value)}
            onFocus={() => setIsOpen(true)}
            onBlur={() => {
              if (blurTimerRef.current !== null)
                clearTimeout(blurTimerRef.current);
              blurTimerRef.current = setTimeout(() => setIsOpen(false), 200);
            }}
            placeholder={placeholder}
          />
        </div>
        <button
          type="button"
          onPointerDown={(e) => e.stopPropagation()}
          onClick={handleClear}
          className={styles.reset}
        >
          X
        </button>
      </div>
      {isOpen && filteredOptions.length > 0 && (
        <div
          className={styles.content}
          style={{
            position: "absolute",
            top: "100%",
            left: 0,
            right: 0,
            zIndex: 1000,
          }}
        >
          <div className={styles.listbox}>
            {filteredOptions.map((option) => (
              <div
                key={String(option.value)}
                className={styles.item}
                onMouseDown={(e) => {
                  e.preventDefault(); // Prevent blur
                  handleSelect(option.value);
                }}
              >
                <span className="flex justify-between w-full">
                  <span>{option.label}</span>
                  {value?.includes(option.value) && <span>✓</span>}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
});
